import React, { useEffect, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import api from '../api';

const fmtMoney = v => `$${(v || 0) >= 0.01 || !v ? (v || 0).toFixed(2) : v.toFixed(4)}`;
const fmtNum = v => (v || 0).toLocaleString();

export default function AdminAnalytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonations, setImpersonations] = useState([]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get('/admin/usage', { params: { days } })
      .then(r => { if (alive) { setData(r.data); setLoading(false); } })
      .catch(() => { if (alive) setLoading(false); });
    api.get('/admin/impersonations', { params: { days } })
      .then(r => { if (alive) setImpersonations(r.data.impersonations); })
      .catch(() => {});
    return () => { alive = false; };
  }, [days]);

  const totals = data?.totals || {};
  const topSenders = (data?.usage || []).slice(0, 10).map(u => ({
    name: u.name.length > 14 ? u.name.slice(0, 14) + '…' : u.name,
    WhatsApp: u.wa_sent,
    Email: u.email_sent,
  }));

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Usage Analytics</h1>
        <div style={{ display: 'flex', gap: 6 }}>
          {[7, 30, 90].map(d => (
            <button key={d}
              className={`btn btn-sm ${days === d ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setDays(d)}>
              {d} days
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={{ color: 'var(--text-muted)', padding: 40, textAlign: 'center' }}>Loading usage…</div>
      ) : !data ? (
        <div style={{ color: 'var(--text-muted)', padding: 40, textAlign: 'center' }}>Could not load usage data.</div>
      ) : (
        <>
          {/* Platform totals */}
          <div className="stat-grid" style={{ marginBottom: 24 }}>
            <StatCard label="WhatsApp Sent" value={fmtNum(totals.wa_sent)} color="var(--primary-light)" />
            <StatCard label="Delivered" value={fmtNum(totals.wa_delivered)} color="var(--success)" />
            <StatCard label="Failed" value={fmtNum(totals.wa_failed)} color="var(--danger)" />
            <StatCard label="Emails Sent" value={fmtNum(totals.email_sent)} />
            <StatCard label="Broadcasts" value={fmtNum(totals.broadcasts)} />
            <StatCard label="Active Senders" value={fmtNum(totals.active_senders)} color="var(--warning)" />
            <StatCard label="Est. WA Spend" value={fmtMoney(totals.est_cost)} color="#22d3ee" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
            {/* Messages per day */}
            <div className="card">
              <div className="card-title">Messages per day (last {data.days} days)</div>
              {data.daily.length === 0 ? (
                <div style={{ color: 'var(--text-muted)', padding: 30, textAlign: 'center' }}>No messages sent in this period</div>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={data.daily}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="day" tick={{ fill: '#94a3b8', fontSize: 11 }}
                      tickFormatter={d => d.slice(5)} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8 }} />
                    <Legend formatter={v => <span style={{ color: '#94a3b8', fontSize: 12 }}>{v}</span>} />
                    <Bar dataKey="whatsapp" name="WhatsApp" stackId="a" fill="#6366f1" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="email" name="Email" stackId="a" fill="#22c55e" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Top senders */}
            <div className="card">
              <div className="card-title">Top senders</div>
              {topSenders.length === 0 ? (
                <div style={{ color: 'var(--text-muted)', padding: 30, textAlign: 'center' }}>No usage yet</div>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={topSenders} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 11 }} allowDecimals={false} />
                    <YAxis type="category" dataKey="name" width={110} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                    <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8 }} />
                    <Bar dataKey="WhatsApp" stackId="a" fill="#6366f1" />
                    <Bar dataKey="Email" stackId="a" fill="#22c55e" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Per-user table */}
          <div className="card">
            <div className="card-title">Usage by user</div>
            {data.usage.length === 0 ? (
              <div className="empty-state" style={{ padding: 40 }}>No broadcast activity in this period</div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>User</th><th>Plan</th><th>Broadcasts</th>
                      <th>WA Sent</th><th>Delivered</th><th>Read</th><th>Failed</th>
                      <th>Emails</th><th>Delivery Rate</th><th>Est. Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.usage.map(u => {
                      const rate = u.wa_sent ? ((u.wa_delivered / u.wa_sent) * 100).toFixed(1) : null;
                      return (
                        <tr key={u.user} style={{ opacity: u.isActive ? 1 : 0.5 }}>
                          <td>
                            <div style={{ fontWeight: 500 }}>{u.name}</div>
                            <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>{u.email}</div>
                          </td>
                          <td><span className="badge badge-blue">{u.plan}</span></td>
                          <td>{fmtNum(u.broadcasts)}</td>
                          <td style={{ fontWeight: 600 }}>{fmtNum(u.wa_sent)}</td>
                          <td>{fmtNum(u.wa_delivered)}</td>
                          <td>{fmtNum(u.wa_read)}</td>
                          <td style={{ color: u.wa_failed ? 'var(--danger)' : undefined }}>{fmtNum(u.wa_failed)}</td>
                          <td>{fmtNum(u.email_sent)}</td>
                          <td>
                            {rate === null ? '—' : (
                              <span className={`badge ${rate >= 90 ? 'badge-green' : rate >= 70 ? 'badge-yellow' : 'badge-red'}`}>{rate}%</span>
                            )}
                          </td>
                          <td>{fmtMoney(u.est_cost)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <p style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 12, marginBottom: 0 }}>
              Counts come from broadcast delivery stats within the selected period.
              Est. cost uses the pricing snapshot taken when each broadcast was queued; email is free (users' own SMTP).
            </p>
          </div>

          {/* Impersonation audit trail */}
          <div className="card" style={{ marginTop: 24 }}>
            <div className="card-title">🔑 Login-as activity (last {days} days)</div>
            {impersonations.length === 0 ? (
              <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: '10px 0' }}>
                No admin logged in as a user in this period.
              </div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr><th>When</th><th>Admin</th><th>Logged in as</th></tr>
                  </thead>
                  <tbody>
                    {impersonations.map(log => (
                      <tr key={log._id}>
                        <td style={{ whiteSpace: 'nowrap' }}>{new Date(log.createdAt).toLocaleString()}</td>
                        <td>
                          <div style={{ fontWeight: 500 }}>{log.actor_name}</div>
                          <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>{log.actor_email}</div>
                        </td>
                        <td>
                          <div style={{ fontWeight: 500 }}>{log.target_name}</div>
                          <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>{log.target_email}</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 12, marginBottom: 0 }}>
              Every use of "Login as user" is recorded, including logins by other super admins.
              Names are snapshotted at the time of the login, so entries survive account changes.
            </p>
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div className="card" style={{ padding: '14px 18px' }}>
      <div style={{ fontSize: 22, fontWeight: 700, color: color || 'var(--text)' }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{label}</div>
    </div>
  );
}
